import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fdate, mxn } from "@/lib/format";
import { DOC_POLIZA_PRORROGA, rentaEstimada } from "@/lib/arrendamiento";
import { FilasResumen, OtDialog, SubirPdfOt, useAccionOt } from "./OtShared";

const POLIZA0 = { aseguradora: "", numero_poliza: "", vigencia_hasta: "", beneficiario_preferente: false };

function CamposCondiciones({ ot, c, set }) {
  const rg = ot.rangos || {};
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div>
        <Label className="mb-1 block text-xs">Plazo (rentas)</Label>
        <Select value={String(c.plazo || "")} onValueChange={(v) => set("plazo", Number(v))}>
          <SelectTrigger data-testid="ot-prorroga-plazo"><SelectValue placeholder="Elige" /></SelectTrigger>
          <SelectContent>{(rg.plazos_permitidos || []).map((p) => <SelectItem key={p} value={String(p)}>{p}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="mb-1 block text-xs">Tasa anual % ({rg.tasa_min ?? "—"}–{rg.tasa_max ?? "—"})</Label>
        <Input data-testid="ot-prorroga-tasa" type="number" value={c.tasa_ordinaria} onChange={(e) => set("tasa_ordinaria", e.target.value)} />
      </div>
      <div>
        <Label className="mb-1 block text-xs">Residual % (0 o {rg.residual_min_pct ?? "—"}–{rg.residual_max_pct ?? "—"})</Label>
        <Input data-testid="ot-prorroga-residual" type="number" value={c.residual_pct} onChange={(e) => set("residual_pct", e.target.value)} />
      </div>
    </div>
  );
}

function CamposPoliza({ ot, creditoId, p, set }) {
  return (
    <div className="space-y-2 rounded-md border border-slate-200 p-3" data-testid="ot-prorroga-poliza">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Póliza de seguro del bien (obligatoria)</p>
      <SubirPdfOt creditoId={creditoId} tipo={DOC_POLIZA_PRORROGA} etiqueta="Póliza de seguro vigente" doc={p.doc}
        onDoc={(d) => set("doc", d)} testid="ot-prorroga-poliza-pdf" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div><Label className="mb-1 block text-xs">Aseguradora</Label>
          <Input data-testid="ot-prorroga-aseguradora" value={p.aseguradora} onChange={(e) => set("aseguradora", e.target.value)} /></div>
        <div><Label className="mb-1 block text-xs">Número de póliza</Label>
          <Input data-testid="ot-prorroga-numero-poliza" value={p.numero_poliza} onChange={(e) => set("numero_poliza", e.target.value)} /></div>
        <div><Label className="mb-1 block text-xs">Vigente hasta</Label>
          <Input data-testid="ot-prorroga-vigencia" type="date" value={p.vigencia_hasta} onChange={(e) => set("vigencia_hasta", e.target.value)} /></div>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <Switch data-testid="ot-prorroga-beneficiario" checked={p.beneficiario_preferente} onCheckedChange={(v) => set("beneficiario_preferente", v)} />
        La arrendadora es beneficiario preferente en la póliza
      </label>
      <p className="text-[11px] text-slate-400">La vigencia debe cubrir al menos la primera renta de la prórroga.</p>
    </div>
  );
}

// Prorrogar: solicitud precargada al Comité; autorizada, el nuevo crédito se contrata solo (sin desembolso).
export function ProrrogaDialog({ open, onOpenChange, ot, creditoId, onDone }) {
  const [c, setC] = useState({});
  const [p, setP] = useState(POLIZA0);
  const { busy, ejecutar } = useAccionOt(creditoId, onDone);
  useEffect(() => {
    if (open) { setC({ plazo: (ot.rangos?.plazos_permitidos || [])[0], tasa_ordinaria: ot.tasa_actual ?? "", residual_pct: 0 }); setP(POLIZA0); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- solo al abrir: un refresco del resumen no borra la captura
  const setCampo = (k, v) => setC((x) => ({ ...x, [k]: v }));
  const setPol = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const residual = Math.round(Number(ot.residual) * Number(c.residual_pct || 0)) / 100;
  const renta = rentaEstimada(ot.residual, residual, c.plazo, c.tasa_ordinaria, ot.rangos?.periodicidad);
  const listo = c.plazo && c.tasa_ordinaria !== "" && p.doc && p.aseguradora && p.numero_poliza && p.vigencia_hasta && p.beneficiario_preferente;
  const body = { plazo: c.plazo, tasa_ordinaria: Number(c.tasa_ordinaria), residual_pct: Number(c.residual_pct || 0),
    poliza: { documento_id: p.doc?.id, aseguradora: p.aseguradora, numero_poliza: p.numero_poliza,
      vigencia_hasta: p.vigencia_hasta, beneficiario_preferente: p.beneficiario_preferente } };
  return (
    <OtDialog open={open} onOpenChange={onOpenChange} testid="ot-prorroga-dialog" titulo="Prorrogar el arrendamiento"
      descripcion={`El nuevo crédito financia el residual ${mxn(ot.residual)} SIN IVA; el IVA se causa en las nuevas rentas.`}
      puedeRevisar={!!listo} busy={busy} textoConfirmar="Enviar al Comité"
      onConfirmar={async () => { if (await ejecutar("prorroga", body, "Prórroga enviada al Comité de Crédito")) onOpenChange(false); }}
      form={<><CamposCondiciones ot={ot} c={c} set={setCampo} />
        <p className="text-xs text-slate-600" data-testid="ot-prorroga-renta-estimada">Renta estimada (sin IVA): <b>{renta != null ? mxn(renta) : "—"}</b>
          · la definitiva la fija el sistema al contratar.</p>
        <CamposPoliza ot={ot} creditoId={creditoId} p={p} set={setPol} /></>}
      resumen={<>
        <FilasResumen testid="ot-prorroga-resumen" filas={[["Capital a prorrogar (sin IVA)", mxn(ot.residual), true],
          ["Plazo", `${c.plazo} rentas (${ot.rangos?.periodicidad || "Mensual"})`], ["Tasa anual", `${c.tasa_ordinaria} %`],
          ["Valor residual de la prórroga", `${c.residual_pct || 0} % · ${mxn(residual)}`],
          ["Renta estimada (sin IVA)", renta != null ? mxn(renta) : "—"],
          ["Póliza", `${p.aseguradora} · ${p.numero_poliza} · vigente al ${fdate(p.vigencia_hasta)}`]]} />
        <p className="text-xs text-slate-600">Va al Comité de Crédito con estas condiciones. Mientras se decide, Comprar y Devolver quedan
          bloqueados. Si el Comité autoriza, el nuevo crédito se contrata solo y el residual del original queda «Prorrogada»;
          si la rechaza, las tres opciones se liberan.</p>
      </>} />
  );
}
